import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ThumbDownIcon from "@mui/icons-material/ThumbDown";
import ThumbDownOutlinedIcon from "@mui/icons-material/ThumbDownOffAlt";
import ThumbUpIcon from "@mui/icons-material/ThumbUp";
import ThumbUpOutlinedIcon from "@mui/icons-material/ThumbUpOffAlt";
import {
    Box,
    Chip,
    IconButton,
    Paper,
    TextField,
    Typography,
    useTheme,
} from "@mui/material";
import AcxButton from "components/UI/AcxButton/AcxButton";
import { observer } from "mobx-react";
import React, { useState } from "react";
import type {
    ResearchFeedbackSentiment,
    ResearchQuestion,
} from "models/ResearchFindings/ResearchFindings";
import type ResearchFindingStore from "./ResearchFindingStore";
import AnswerSection from "./AnswerSection";
import QuestionFeedbackList from "./QuestionFeedbackList";
import WhyPassedSection from "./WhyPassedSection";
import { questionStatusColor } from "./researchFindingsUtils";

type Props = {
    question: ResearchQuestion;
    store: ResearchFindingStore;
};

const ResearchQuestionCard: React.FC<Props> = observer(({ question, store }) => {
    const theme = useTheme();
    const [pending, setPending] = useState<ResearchFeedbackSentiment | null>(
        null,
    );
    const [comment, setComment] = useState("");
    const [showAllFeedback, setShowAllFeedback] = useState(false);
    const [feedbackRefreshToken, setFeedbackRefreshToken] = useState(0);

    // The visibly-selected thumb: the pending pick while editing, else the committed sentiment.
    const activeSentiment = pending ?? question.feedback?.sentiment;
    const upActive = activeSentiment === "Positive";
    const downActive = activeSentiment === "Negative";
    const feedbackSummary = question.feedbackSummary ?? {
        positiveCount: 0,
        negativeCount: 0,
    };

    const onThumb = (sentiment: ResearchFeedbackSentiment) => {
        setPending(sentiment);
        setComment(question.feedback?.text ?? "");
    };

    const submit = async () => {
        if (!pending) return;
        await store.submitFeedback(question.id, pending, comment.trim() || undefined);
        setPending(null);
        setComment("");
        // Reload the expanded history (if open) so it includes the new entry.
        setFeedbackRefreshToken((t) => t + 1);
    };

    return (
        <Paper
            elevation={0}
            sx={{
                padding: theme.spacing(2.5),
                borderRadius: "8px",
                border: `1px solid ${theme.palette.gray[200]}`,
            }}
        >
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                    gap: 2,
                }}
            >
                <Box>
                    <Typography
                        sx={{
                            fontSize: "11px",
                            fontWeight: 600,
                            color: theme.palette.gray[500],
                            textTransform: "uppercase",
                            letterSpacing: "0.04em",
                        }}
                    >
                        The question
                    </Typography>
                    <Typography
                        sx={{
                            fontSize: "16px",
                            fontWeight: 600,
                            color: theme.palette.gray[700],
                            mt: 0.25,
                        }}
                    >
                        {question.text}
                    </Typography>
                </Box>
                <Chip
                    size="small"
                    label={question.status}
                    color={questionStatusColor(question.status)}
                    variant="outlined"
                    sx={{ flexShrink: 0 }}
                />
            </Box>

            {question.reason && (
                <Typography
                    sx={{
                        fontSize: "13px",
                        color: theme.palette.gray[500],
                        mt: 0.75,
                    }}
                >
                    {question.reason}
                </Typography>
            )}

            {question.validation && (
                <WhyPassedSection validation={question.validation} />
            )}

            {/* Question feedback */}
            <Box sx={{ mt: 1.5, display: "flex", alignItems: "center", gap: 1 }}>
                <Typography
                    sx={{ fontSize: "13px", color: theme.palette.gray[600] }}
                >
                    Was this a good question to research?
                </Typography>
                <IconButton
                    size="small"
                    color={upActive ? "primary" : "default"}
                    onClick={() => onThumb("Positive")}
                    aria-label="Thumbs up"
                >
                    {upActive ? (
                        <ThumbUpIcon fontSize="small" />
                    ) : (
                        <ThumbUpOutlinedIcon fontSize="small" />
                    )}
                </IconButton>
                <Typography
                    sx={{ fontSize: "12px", color: theme.palette.gray[500] }}
                >
                    {feedbackSummary.positiveCount}
                </Typography>
                <IconButton
                    size="small"
                    color={downActive ? "error" : "default"}
                    onClick={() => onThumb("Negative")}
                    aria-label="Thumbs down"
                >
                    {downActive ? (
                        <ThumbDownIcon fontSize="small" />
                    ) : (
                        <ThumbDownOutlinedIcon fontSize="small" />
                    )}
                </IconButton>
                <Typography
                    sx={{ fontSize: "12px", color: theme.palette.gray[500] }}
                >
                    {feedbackSummary.negativeCount}
                </Typography>
            </Box>

            {pending && (
                <Box sx={{ mt: 1, display: "flex", flexDirection: "column", gap: 1 }}>
                    <TextField
                        size="small"
                        fullWidth
                        multiline
                        minRows={2}
                        placeholder="Add an optional comment"
                        value={comment}
                        onChange={(e) => setComment(e.target.value)}
                    />
                    <Box sx={{ display: "flex", gap: 1 }}>
                        <AcxButton
                            color="primary"
                            onClick={submit}
                            fullWidth={false}
                        >
                            Submit feedback
                        </AcxButton>
                        <AcxButton
                            color="white"
                            onClick={() => {
                                setPending(null);
                                setComment("");
                            }}
                            fullWidth={false}
                        >
                            Cancel
                        </AcxButton>
                    </Box>
                </Box>
            )}

            {/* All feedback history */}
            <Box sx={{ mt: 1 }}>
                <AcxButton
                    color="white"
                    fullWidth={false}
                    variant="text"
                    endIcon={
                        showAllFeedback ? (
                            <ExpandLessIcon />
                        ) : (
                            <ExpandMoreIcon />
                        )
                    }
                    onClick={() => setShowAllFeedback((v) => !v)}
                >
                    {showAllFeedback ? "Hide feedback" : "View all feedback"}
                </AcxButton>
                {showAllFeedback && (
                    <QuestionFeedbackList
                        store={store}
                        questionId={question.id}
                        refreshToken={feedbackRefreshToken}
                    />
                )}
            </Box>

            {question.answer && <AnswerSection answer={question.answer} />}

            {question.answer?.hasEmail && (
                <Box sx={{ mt: 2 }}>
                    <AcxButton
                        color="primary"
                        fullWidth={false}
                        startIcon={<EmailOutlinedIcon />}
                        onClick={() => store.openEmail(question.id)}
                    >
                        Preview &amp; send client email
                    </AcxButton>
                </Box>
            )}
        </Paper>
    );
});

export default ResearchQuestionCard;
